import { useCallback, useMemo, useRef, useState } from 'react';
import { useLang } from 'hooks/lang';

import {
  actions,
  loaders,
  isOwner,
  ProjectRepriceRequest,
  ProjectService,
  getServiceName,
  projectStatusConfig,
} from 'entities';

import { Skeleton, LabeledTextarea, Column, Row, Card, Input, Label } from 'components/Blocks';
import { ApproveRejectButtons } from 'components/Buttons';
import { PercentageVariation } from 'components/PercentageVariation';
import { ArrowRightIcon, EarthIcon, ShoppingCartIcon } from 'components/Icons';

import { PricedBy } from '../InvoiceRequestedBy';
import { FromDifferenceSource } from '../FromDifferenceSource';
import { FullyInvoicedRepriceAlert } from 'pages/Invoice/components';
import { ServiceOutsourcedTag } from 'pages/Projects/components';

import { PriceChange } from 'pages/PendingActions/RepriceRequest/PriceChange';
import { ConfirmPricesButton } from './ConfirmButton';

import { useProjectProfile } from '../../../context';
import { useUser } from 'hooks/user';

import { getJoinedKey, verifyFilling, updateServicesWithControl, mergeServices } from '../utils';
import { formatCurrency } from 'utils/text';
import { getCountryLabel } from 'utils/countries';
import { cn } from 'styles';

export function ApproveForm() {
  const { t } = useLang();
  const user = useUser();
  const {
    project,
    projectModalConfig: { closeModal },
    updateProject,
  } = useProjectProfile();

  const { data, error, loading } = loaders.projects.getProjectsActiveReprice.use({
    blockLoad: !project,

    defaultValue: {} as ProjectRepriceRequest,

    params: {
      projectId: project?.id as string,
    },
  });

  const [control, setControl] = useState<Record<string, boolean>>({});

  const commentRef = useRef<HTMLTextAreaElement>(null);

  const { repricedServices, repricerComment, id: repriceId } = data;

  const canSubmit = verifyFilling(repricedServices, control);

  const acceptRepriceRequest = actions.projects.acceptProjectReprice.use({
    block: !canSubmit,

    params: {
      repriceId,
      comment: commentRef.current?.value || '',
      project: project.id,
      repricedServices: repricedServices
        ? updateServicesWithControl(repricedServices, control)
        : [],
    },

    onSuccess: (updatedServices) => {
      closeModal();

      const fromInvoice = project.repriceOrigin === 'INVOICE';

      updateProject((old) => ({
        ...old,
        reprice: 'COMPLETED',
        repriceOrigin: null,
        status: fromInvoice ? old.status : projectStatusConfig.started.code,
        services: updatedServices,
      }));
    },
  });

  const changeApprovalStatus = useCallback(
    (serviceId: string, country: string, newStatus: boolean) => {
      const key = getJoinedKey(serviceId, country);

      setControl((old) => ({
        ...old,
        [key]: newStatus,
      }));
    },
    [],
  );

  const changeOutsourceApproval = useCallback(
    (serviceId: string, newStatus: boolean) => {
      const service = repricedServices?.find(({ id }) => id === serviceId);
      const keys = service?.costs.map(({ country }) => getJoinedKey(serviceId, country)) || [];

      setControl((old) => ({
        ...old,
        ...Object.fromEntries(keys.map((key) => [key, newStatus])),
      }));
    },
    [repricedServices],
  );

  const oldCosts = useMemo(() => {
    return Object.fromEntries(
      project.services
        .map(({ costs, id }) =>
          Object.entries(costs).map(([country, countryCosts]) => [
            getJoinedKey(id, country),
            countryCosts,
          ]),
        )
        .flat(),
    ) as Record<string, ProjectService['costs'][string]>;
  }, [project]);

  if (error || loading) return <Skeleton quantity={4} height={42} />;

  const otherSourceAccess =
    project.repriceOrigin === 'INVOICE' ? !user.team.isInvoiceTeam : !isOwner(project, user);

  if (otherSourceAccess) return <FromDifferenceSource />;

  return (
    <>
      <Column gap="6">
        {repricedServices?.map(({ id, costs, outsourceConfig }) => (
          <Column gap="2" key={`service-${id}`}>
            <Row gap="2">
              <p className="text-lg font-semibold text-primary">
                {getServiceName(id, project.tasks)}
              </p>

              {!!outsourceConfig && <ServiceOutsourcedTag />}
            </Row>

            <Column>
              {costs.map(({ costs, country }) => (
                <Card flow="column" key={`service-${id}-${country}`} fullWidth>
                  <Row>
                    <EarthIcon />

                    <p className="font-medium">{getCountryLabel(country)}</p>

                    <div className="ml-auto">
                      <ApproveRejectButtons
                        value={control[getJoinedKey(id, country)] ?? null}
                        onChange={(value) => changeApprovalStatus(id, country, value)}
                        disabled={!!outsourceConfig}
                        className="p-1"
                      />
                    </div>
                  </Row>

                  <div className="grid grid-cols-auto-150 gap-4">
                    {costs.map(({ name, value }, index) => {
                      const oldCost = oldCosts[getJoinedKey(id, country)][index];
                      const valueChanged = oldCost.value !== value;

                      return (
                        <Column gap="0" key={`service-${id}-${country}--${name}`}>
                          <Row gap="3">
                            <Label>{name}</Label>
                            <span className="text-sm">
                              <PercentageVariation newValue={value} current={oldCost.value} />
                            </span>
                          </Row>

                          <Row gap="2">
                            <span className={cn('font-medium', valueChanged && 'text-sm')}>
                              {formatCurrency(oldCost.value, oldCost.currency)}
                            </span>

                            {valueChanged && (
                              <>
                                <ArrowRightIcon size={14} color="primary" />

                                <span className="font-medium text-primary">{value}</span>
                              </>
                            )}
                          </Row>
                        </Column>
                      );
                    })}
                  </div>
                </Card>
              ))}

              {!!outsourceConfig && (
                <Card flow="column" fullWidth gap="6">
                  <Row>
                    <ShoppingCartIcon />

                    <p className="font-medium">{t('outsource-settings')}</p>

                    <div className="ml-auto">
                      <ApproveRejectButtons
                        className="p-1"
                        onChange={(value) => changeOutsourceApproval(id, value)}
                        value={
                          Object.entries(control).length
                            ? !!Object.entries(control).find(
                                ([key, accepted]) => key.startsWith(id) && accepted,
                              )
                            : null
                        }
                      />
                    </div>
                  </Row>

                  <div className="grid grid-cols-auto-150 gap-4">
                    <Column gap="1">
                      <Label>{t('provider-name')}</Label>
                      <Input value={outsourceConfig?.providerName} disabled />
                    </Column>

                    <Column gap="1">
                      <Label>{t('provider-cost')}</Label>
                      <Input value={outsourceConfig?.providerCost.value} disabled />
                    </Column>

                    <Column gap="1">
                      <Label>{t('cost-to-client')}</Label>
                      <Input value={outsourceConfig?.clientCost.value} disabled />
                    </Column>
                  </div>
                </Card>
              )}
            </Column>
          </Column>
        ))}

        <PriceChange
          currentServices={project.services}
          currency={project.currency}
          // For some reason passing just control would pass in undefined?
          repricedServices={mergeServices(repricedServices!, project.services, { ...control })}
        />

        <Row alignItems="flex-start">
          <div className="mt-2">
            <PricedBy userId={data.repricedBy as string} />
          </div>

          <div className="w-full rounded-lg border bg-muted p-2">{repricerComment || 'N/A'}</div>
        </Row>

        <LabeledTextarea label={t('add-comments')} ref={commentRef} />
      </Column>

      <FullyInvoicedRepriceAlert invoice={project.invoice} />

      <ConfirmPricesButton
        {...acceptRepriceRequest.buttonProps}
        disabled={!canSubmit}
        currentServices={project.services}
        currency={project.currency}
        repricedServices={mergeServices(repricedServices!, project.services, { ...control })}
      />
    </>
  );
}
